import { Button, Checkbox, ButtonGroup } from "@blueprintjs/core";
import {
  DataTable,
  FieldSet,
  IDataTableColumn,
  showError,
  showSuccess,
  useGridState,
  useSearchApi,
} from "nsitools-react";
import * as React from "react";

import { MembershipRenewalApi, MembershipRenewalGridDto, MembershipRenewalSearch } from "../../../../api";
import { PageBase, ExportButton } from "../../../../components";
import { useApiService, useTl } from "../../../../hooks";
import { ETLCodes } from "../../../../locales";
import { useDialog } from "../../../../contexts";
import { Link } from "react-router-dom";
import { ERoutes } from "../../../../ERoutes";

export interface IMembershipRenewalListPageProps {}

export const MembershipRenewalListPage: React.FunctionComponent<IMembershipRenewalListPageProps> = (props) => {
  const api = useApiService(MembershipRenewalApi);
  const { t } = useTl();
  const [sObjState, setsobjState] = React.useState<any>();

  const tableState = useGridState<any>({
    serverMode: true,
    enablePagination: true,
    availablePageSizes: [5, 15, 25],
    pageSize: 25,
  });
  const { showDialog } = useDialog();
  const searchFunction = React.useCallback(
    (sObj?: MembershipRenewalSearch) => {
      setsobjState(sObj);
      return api.baseSearch(sObj);
    },
    [api]
  );

  const { search, loading } = useSearchApi<any, any>({
    searchFunction,
    tableState,
    initialSearch: true,
    trimSearchObjectStringValues: true,
  });
  const { setData, data, totalCount } = tableState;

  const forceMembership = React.useCallback(
    async (row: MembershipRenewalGridDto, value: boolean) => {
      const dataToSet = [...data];
      const index = dataToSet.findIndex((d) => d.memberId === row.memberId);
      dataToSet[index]["autoRenewal"] = !value;
      setData(dataToSet);
      return await api.forceRenewal(row.memberId, !value);
    },
    [api, data, setData]
  );

  const columns = React.useMemo<IDataTableColumn[]>(
    () => [
      {
        computed: true,
        disableSorting: true,
        fieldName: "autoRenewal",
        autoFitContent: true,
        alignment: "center",
        render: (row: MembershipRenewalGridDto) => (
          <Checkbox
            checked={row.autoRenewal}
            disabled={!!row.deathDate}
            onClick={() => forceMembership(row, row.autoRenewal)}
          />
        ),
      },
      {
        computed: true,
        disableSorting: true,
        header: () => t(ETLCodes.Member),
        fieldName: "member",
        render: (row: MembershipRenewalGridDto) => (
          <Link target="_blank" to={`${ERoutes.people}/${row.personId}/detail/view`}>
            {" "}
            {row.member}{" "}
          </Link>
        ),
      },
      {
        computed: true,
        disableSorting: true,
        header: () => t(ETLCodes.DeathDate),
        fieldName: "deathDate",
      },
      {
        computed: true,
        disableSorting: true,
        header: () => t(ETLCodes.EndOfRenewal),
        fieldName: "endOfRenewalAsked",
        alignment: "center",
        autoFitContent: true,
        render: (row: MembershipRenewalGridDto) => <Checkbox checked={row.endOfRenewalAsked} disabled />,
      },
    ],
    [forceMembership, t]
  );

  const createRequests = React.useCallback(async () => {
    try {
      showDialog({
        message: t(ETLCodes.CreateRequestsMessage),
        title: t(ETLCodes.CreateRenewRequestsTitle),
        onConfirmed: async () => {
          const res = await api.createRequests();
          showSuccess(t(ETLCodes.RequestCreationSuccess));
          search();
          return res;
        },
      });
    } catch (err) {
      showError(t(ETLCodes.Error));
    }
  }, [api, search, showDialog, t]);

  return (
    <PageBase withCard={true} breadCrumbs={[{ text: t(ETLCodes.MembershipRenewals) }]}>
      <FieldSet
        title={
          <div style={{ display: "flex", flexDirection: "column" }}>
            <span style={{ fontSize: "1.5rem", fontWeight: "bold", marginLeft: "0.5rem", marginBottom: "0.5rem" }}>
              {new Date().getFullYear()}
            </span>
            <ButtonGroup>
              <ExportButton exportFunction={(sObj) => api.exportXlsFile(sObj)} sObj={sObjState} />
              <Button
                icon={<i className="fas fa-folder" />}
                text={t(ETLCodes.CreateRequests)}
                intent={"primary"}
                onClick={() => createRequests()}
                minimal={true}
              />
            </ButtonGroup>
          </div>
        }
        rightElement={
          <>
            {totalCount > 1
              ? t(ETLCodes.TableResults_Plural, { count: totalCount })
              : t(ETLCodes.TableResults, { count: totalCount })}
          </>
        }
      >
        <DataTable dateFormat="DD/MM/YYYY" tableState={tableState} loading={loading} columns={columns} />
      </FieldSet>
    </PageBase>
  );
};
